import { Component, computed, inject, input, linkedSignal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';

import { ProductItem } from '../../model/product-item';
import { ProductPage } from '../../model/product-page';
import { AdminService } from '../../service/admin.service';
import { BuyService } from '../../service/buy.service';
import { PageFailure } from '../../service/page-failure';
import { SeoService } from '../../service/seo.service';
import { AdminProductPanelComponent } from '../admin/admin-product-panel.component';
import { BreadcrumbComponent } from '../product/breadcrumb.component';
import { ProductItemComponent } from '../product/product-item.component';
import { ProductSelectComponent } from '../product/product-select.component';
import { PhotoSliderComponent } from '../slider/photo-slider.component';
import { ProductSliderComponent } from '../slider/product-slider.component';
import { MoneyPipe } from '../../money.pipe';

@Component({
  selector: 'product-page',
  imports: [
    RouterLink, BreadcrumbComponent, ProductSelectComponent, ProductItemComponent,
    AdminProductPanelComponent, PhotoSliderComponent, ProductSliderComponent, MoneyPipe
  ],
  host: {
    style: 'display: contents'
  },
  template: `
    @if (data().product) {
      <breadcrumb [product]="product()" />

      @if (isAdmin()) {
        <div class="container">
          <admin-product-panel [product]="product()" (changed)="edited($event)" />
          @if (refreshFailure(); as refreshFailure) {
            <p class="s-text5 text-danger p-b-10">{{refreshFailure}}</p>
          }
        </div>
      }

      <!-- Product Detail -->
      <div class="container bgwhite p-t-35 p-b-80">
        <div class="flex-w flex-sb">
          <div class="w-size13 p-t-30 respon5">
            <photo-slider [photos]="product().photos" [alt]="product().name" />
          </div>

          <div class="w-size14 p-t-30 respon5">
            <h1 field="name" class="product-detail-name m-text16 p-b-13">
              {{product().name}}
            </h1>

            <span field="price" class="m-text17">{{product().price | money}}</span>

            <div class="p-t-33 p-b-60">
              @for (select of data().selects; track $index) {
                <product-select [select]="select" />
              }

              <div class="flex-r-m flex-w p-t-10">
                @if (data().callToAction; as callToAction) {
                  <div class="w-size16 flex-m flex-w">
                    <div class="flex-w of-hidden m-r-22 m-t-10 m-b-10">
                      {{callToAction}}
                    </div>
                  </div>
                }
                @if (data().buyable) {
                  <div class="w-size16 flex-m flex-w d-none d-lg-flex">
                    <div class="flex-w bo5 of-hidden m-r-22 m-t-10 m-b-10">
                      <button class="btn-num-product-down color1 flex-c-m size7 bg8 eff2" (click)="change(-1)">
                        <i class="fs-12 fa fa-minus" aria-hidden="true"></i>
                      </button>

                      <input class="size8 m-text18 t-center num-product" type="number" name="num-product"
                        [value]="quantity()" (input)="quantity.set(+$any($event.target).value)">

                      <button class="btn-num-product-up color1 flex-c-m size7 bg8 eff2" (click)="change(1)">
                        <i class="fs-12 fa fa-plus" aria-hidden="true"></i>
                      </button>
                    </div>

                    <div class="btn-addcart-product-detail size9 trans-0-4 m-t-10 m-b-10">
                      <!-- Button -->
                      <button type="button" class="flex-c-m sizefull bg1 bo-rad-23 hov1 s-text1 trans-0-4 buy"
                        [id]="product().id" [name]="product().name"
                        [attr.price]="product().price?.value" (click)="buy()">
                        Купити
                      </button>
                    </div>
                  </div>
                  <div class="sizefull flex-m flex-w d-flex d-lg-none">
                    <div class="btn-addcart-product-detail size9 w-100 trans-0-4 m-t-10 m-b-10">
                      <!-- Button -->
                      <button type="button" class="flex-c-m sizefull bg1 bo-rad-23 hov1 s-text1 trans-0-4 buy"
                        [id]="product().id" [name]="product().name"
                        [attr.price]="product().price?.value" (click)="buy()">
                        Купити
                      </button>
                    </div>
                  </div>
                }
              </div>
            </div>

            <div class="p-b-45">
              @if (product().code) {
                <span class="s-text8 m-r-35">Код товару:
                  <span field="code">{{product().code}}</span>
                </span>
              }
              @if (product().category) {
                <span class="s-text8">Категорія:
                  <span>{{product().category!.name}}</span>
                </span>
              }
            </div>

            @if (product().text) {
              <div class="wrap-dropdown-content bo6 p-t-15 p-b-14 active-dropdown-content">
                <h5 class="js-toggle-dropdown-content flex-sb-m cs-pointer m-text19 color0-hov trans-0-4">
                  Опис товару
                  <i class="down-mark fs-12 color1 fa fa-minus dis-none" aria-hidden="true"></i>
                  <i class="up-mark fs-12 color1 fa fa-plus" aria-hidden="true"></i>
                </h5>
                <div class="dropdown-content dis-none p-t-15 p-b-23">
                  <p class="s-text8" field="text" [innerHTML]="product().html"></p>
                </div>
              </div>
            }
            @if (product().parameters.length) {
              <div class="wrap-dropdown-content bo6 p-t-15 p-b-14 active-dropdown-content">
                <h5 class="js-toggle-dropdown-content flex-sb-m cs-pointer m-text19 color0-hov trans-0-4">
                  Характеристики
                  <i class="down-mark fs-12 color1 fa fa-minus dis-none" aria-hidden="true"></i>
                  <i class="up-mark fs-12 color1 fa fa-plus" aria-hidden="true"></i>
                </h5>
                <div class="dropdown-content dis-none p-t-15 p-b-23">
                  @for (parameter of product().parameters; track $index) {
                    <div class="row">
                      <div class="col">
                        <span class="s-text8">{{parameter.label}}</span>
                      </div>
                      <div class="col">
                        <span class="s-text8">{{parameter.value}}</span>
                      </div>
                    </div>
                  }
                </div>
              </div>
            }

            <div class="wrap-dropdown-content bo7 p-t-15 p-b-14">
              <h5 class="js-toggle-dropdown-content flex-sb-m cs-pointer m-text19 color0-hov trans-0-4">
                Додаткова інформація
                <i class="down-mark fs-12 color1 fa fa-minus dis-none" aria-hidden="true"></i>
                <i class="up-mark fs-12 color1 fa fa-plus" aria-hidden="true"></i>
              </h5>

              <div class="dropdown-content dis-none p-t-15 p-b-23">
                <p class="s-text8">
                  Список товарів, які не підлягають поверненню у 14-денний термін, можна прочитати
                  <a routerLink="/questions" fragment="return-policy"><u>на сторінці запитань</u></a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      @if (product().recommendations?.length) {
        <!-- Relate Product -->
        <section class="relateproduct bgwhite p-t-45 p-b-138">
          <div class="container">
            <div class="sec-title p-b-60">
              <h3 class="m-text5 t-center">
                Рекомендуємо переглянути
              </h3>
            </div>

            <!-- Slide2 -->
            <product-slider>
              @for (recommendation of product().recommendations!; track recommendation.id) {
                <div class="item-slick2 p-l-15 p-r-15">
                  <product-item [product]="recommendation" />
                </div>
              }
            </product-slider>
          </div>
        </section>
      }
    } @else {
      <section class="bg-title-page p-t-15 p-b-16 flex-col-c-m">
        <h1 class="l-text2 t-center">
          Товар не знайдено
        </h1>
      </section>

      <section class="bgwhite p-t-70 p-b-100">
        <div class="container">
          <p class="text-center">
            Такого товару немає.
            <a class="fglight" routerLink="/products">Подивитись інші?</a>
          </p>
        </div>
      </section>
    }
    `
})
export class ProductPageComponent {

  readonly data = input.required<ProductPage>();

  private buyService = inject(BuyService);
  private router = inject(Router);
  private pageFailure = inject(PageFailure);

  readonly isAdmin = inject(AdminService).isAdmin;
  // An admin edit answers with the saved product, and that is what the page shows until it moves
  // to another one: the component is reused from product to product.
  readonly product = linkedSignal(() => this.data().product!);
  readonly quantity = linkedSignal({ source: () => this.data().product, computation: () => 1 });
  readonly refreshFailure = linkedSignal<ProductPage, string | undefined>({
    source: this.data,
    computation: () => undefined
  });

  constructor() {
    inject(SeoService).page(computed(() => this.data().product
      ? this.data()
      : { ...this.data(), title: 'Товар не знайдено', robots: 'noindex' }));
  }

  async edited(product: ProductItem) {
    const before = this.product();
    this.product.set(product);
    // Whether it can be bought, the sentence when it cannot, the title and the address come with
    // the page, and only the status and the uri - built from the name and the category - change them.
    if (product.status === before.status && product.uri === before.uri) {
      return;
    }
    this.refreshFailure.set(undefined);
    try {
      await this.router.navigateByUrl(this.router.url, { onSameUrlNavigation: 'reload' });
    } catch {
      this.pageFailure.message.set(undefined);
      this.refreshFailure.set('Збережено, але сторінку не вдалося оновити. Перезавантажте її.');
    }
  }

  change(delta: number) {
    this.quantity.update(current => Math.max(1, current + delta));
  }

  buy() {
    return this.buyService.buy(this.product().id, this.product().name, this.quantity());
  }

}
